import { useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { 
  Upload, 
  FileText, 
  ArrowLeft, 
  Zap, 
  Flame, 
  Check,
  X,
  Shield
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ad8cae97-e844-4242-bf8a-3cc50b57fa19/artifacts/vj8eyvuz_logo%20%2B%20personaggio%20-%208energy%20srl%20%20%281%29.svg";
const OTTO_URL = "https://static.wixstatic.com/media/709795_dcb4777a39394c9fb86ba6ad9678de09~mv2.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UploadBill() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [step, setStep] = useState("upload");
  const [privacyConsent, setPrivacyConsent] = useState(false);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.type === "application/pdf") {
        setFile(droppedFile);
      } else {
        toast.error("Solo file PDF sono accettati");
      }
    }
  }, []);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      if (selectedFile.type === "application/pdf") {
        setFile(selectedFile);
      } else {
        toast.error("Solo file PDF sono accettati");
      }
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    setStep("analyzing");
    
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + 10;
      });
    }, 500);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(`${API}/bills/upload`, {
        method: "POST",
        credentials: "include",
        body: formData
      });

      clearInterval(progressInterval);
      setProgress(100);

      if (!response.ok) {
        let errorMsg = "Errore durante l'upload";
        try {
          const errorData = await response.clone().json();
          errorMsg = errorData.detail || errorMsg;
        } catch (e) {
          try {
             errorMsg = await response.clone().text(); 
          } catch (e2) { console.error(e2); }
        }
        throw new Error(errorMsg);
      }

      const analysis = await response.json();
      setStep("complete");
      
      // Save guest_token to localStorage if present (for guest users)
      if (analysis.guest_token) {
        localStorage.setItem(`guest_analysis_${analysis.analysis_id}`, analysis.guest_token);
      }
      
      toast.success("Bolletta analizzata con successo!");
      
      setTimeout(() => {
        navigate(`/analysis/${analysis.analysis_id}`);
      }, 1500);
    } catch (error) {
      clearInterval(progressInterval);
      toast.error(error.message);
      setStep("upload");
      setProgress(0);
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }} data-testid="upload-page">
      {/* Navigation */}
      <nav className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <Link to="/">
              <img src={LOGO_URL} alt="8energy" className="h-8" />
            </Link>
            <Link to="/">
              <Button variant="ghost" className="rounded-full text-gray-500 hover:text-gray-900" data-testid="back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Home
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            Carica la tua bolletta
          </h1>
          <p className="text-gray-500 text-lg">
            Carica il PDF e scopri in 60 secondi se stai pagando il giusto prezzo
          </p>
        </motion.div>

        {/* Progress Steps */}
        <motion.div 
          className="flex items-center justify-center gap-4 mb-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          {[
            { key: "upload", label: "Carica" },
            { key: "analyzing", label: "Analisi" },
            { key: "complete", label: "Risultato" }
          ].map((s, i) => (
            <div key={s.key} className="flex items-center gap-3">
              <div className="flex flex-col items-center gap-2">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                  step === s.key 
                    ? "bg-[#006838] text-white" 
                    : step === "complete" || (step === "analyzing" && i === 0)
                      ? "bg-[#ADFF2F] text-[#006838]"
                      : "bg-gray-100 text-gray-400"
                }`}>
                  {(step === "complete" && i < 2) || (step === "analyzing" && i === 0) ? (
                    <Check className="w-5 h-5" strokeWidth={2} />
                  ) : (
                    i + 1
                  )}
                </div>
                <span className={`text-xs font-medium ${step === s.key ? "text-[#006838]" : "text-gray-400"}`}>
                  {s.label}
                </span>
              </div>
              {i < 2 && (
                <div className={`w-16 h-0.5 mb-6 rounded ${
                  (step === "analyzing" && i === 0) || step === "complete" 
                    ? "bg-[#ADFF2F]" 
                    : "bg-gray-200"
                }`}></div>
              )}
            </div>
          ))}
        </motion.div>

        {/* Upload Area */}
        {step === "upload" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div
              className={`relative rounded-[2rem] border-2 border-dashed transition-all cursor-pointer overflow-hidden ${
                dragActive 
                  ? "border-[#006838] bg-[#ADFF2F]/10" 
                  : file 
                    ? "border-[#006838] bg-green-50"
                    : "border-gray-200 hover:border-[#006838] bg-[#f5f5f7]"
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => document.getElementById("file-input")?.click()}
              data-testid="upload-dropzone"
            >
              <input
                id="file-input"
                type="file"
                accept=".pdf"
                className="hidden"
                onChange={handleFileChange}
                data-testid="file-input"
              />

              <div className="p-12 text-center">
                {file ? (
                  <div className="space-y-4">
                    <div className="w-20 h-20 bg-[#006838] rounded-2xl flex items-center justify-center mx-auto">
                      <FileText className="w-10 h-10 text-white" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-lg">{file.name}</p>
                      <p className="text-sm text-gray-500">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    <button 
                      className="inline-flex items-center gap-1 text-red-500 hover:text-red-600 text-sm font-medium"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFile(null);
                      }}
                      data-testid="remove-file-btn"
                    >
                      <X className="w-4 h-4" strokeWidth={1.5} />
                      Rimuovi
                    </button>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <motion.img 
                      src={OTTO_URL} 
                      alt="OTTO" 
                      className="w-28 mx-auto"
                      animate={{ y: [0, -8, 0] }}
                      transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                    />
                    <div>
                      <p className="font-semibold text-gray-900 text-lg">
                        Trascina qui il file PDF
                      </p>
                      <p className="text-gray-500">
                        oppure clicca per selezionare
                      </p>
                    </div>
                    <div className="flex items-center justify-center gap-6">
                      <div className="flex items-center gap-2 text-gray-500">
                        <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center">
                          <Zap className="w-5 h-5 text-amber-500" strokeWidth={1.5} />
                        </div>
                        <span className="text-sm font-medium">Luce</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-500">
                        <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center">
                          <Flame className="w-5 h-5 text-orange-500" strokeWidth={1.5} />
                        </div>
                        <span className="text-sm font-medium">Gas</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {file && (
              <motion.div
                className="mt-8 space-y-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                {/* Privacy Consent Checkbox */}
                <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl max-w-lg mx-auto">
                  <Checkbox 
                    id="privacy-consent"
                    checked={privacyConsent}
                    onCheckedChange={setPrivacyConsent}
                    className="mt-0.5 data-[state=checked]:bg-[#006838] data-[state=checked]:border-[#006838]"
                    data-testid="privacy-consent-checkbox"
                  />
                  <label htmlFor="privacy-consent" className="text-sm text-gray-600 cursor-pointer leading-relaxed">
                    <Shield className="w-4 h-4 inline mr-1 text-[#006838]" />
                    Acconsento al trattamento dei miei dati (
                    <a 
                      href="https://www.8energy.it/privacy-policy" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-[#006838] underline hover:text-[#005530]"
                      onClick={(e) => e.stopPropagation()}
                    >
                      informativa privacy
                    </a>
                    ) esclusivamente per il calcolo del risparmio.
                  </label>
                </div>

                <div className="text-center">
                  <Button
                    size="lg"
                    className="rounded-full bg-[#006838] hover:bg-[#005530] text-white h-14 px-10 font-semibold shadow-lg shadow-[#006838]/20 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleUpload}
                    disabled={uploading || !privacyConsent}
                    data-testid="analyze-btn"
                  >
                    <Upload className="w-5 h-5 mr-2" strokeWidth={1.5} />
                    Analizza bolletta
                  </Button>
                  {!privacyConsent && (
                    <p className="text-xs text-gray-400 mt-2">
                      Accetta l'informativa privacy per procedere
                    </p>
                  )}
                </div>
              </motion.div>
            )}
          </motion.div>
        )}

        {/* Analyzing State */}
        {step === "analyzing" && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="bg-[#f5f5f7] rounded-[2rem] p-12 text-center">
              <motion.img 
                src={OTTO_URL} 
                alt="OTTO sta analizzando" 
                className="w-32 mx-auto mb-6"
                animate={{ rotate: [0, 5, -5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              />
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Sto analizzando la tua bolletta...
              </h3>
              <p className="text-gray-500 mb-8">
                Dammi qualche secondo, la esamino per filo e per segno!
              </p>
              <div className="max-w-xs mx-auto">
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <motion.div 
                    className="h-full bg-[#006838] rounded-full"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <p className="text-sm text-gray-500 mt-3">{progress}%</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Complete State */}
        {step === "complete" && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="bg-gradient-to-br from-[#ADFF2F]/20 to-[#006838]/10 rounded-[2rem] p-12 text-center border border-[#ADFF2F]/30">
              <div className="w-20 h-20 bg-[#006838] rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-white" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Analisi completata!
              </h3>
              <p className="text-gray-500">
                Reindirizzamento ai risultati...
              </p>
            </div>
          </motion.div>
        )}

        {/* Tips */}
        {step === "upload" && (
          <motion.div
            className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="bg-[#f5f5f7] rounded-2xl p-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#006838] rounded-xl flex items-center justify-center flex-shrink-0">
                  <Check className="w-5 h-5 text-white" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">Consiglio</p>
                  <p className="text-sm text-gray-500">
                    Carica l'ultima bolletta per un'analisi più accurata
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-[#f5f5f7] rounded-2xl p-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-white" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">Formato</p>
                  <p className="text-sm text-gray-500">
                    Accettiamo solo file PDF fino a 10MB
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
}
